import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import type { SectionId } from '#src/app/pages/petitionDetails/shared/hooks';

import { VideoPreviewButton } from '../../components/VideoPreviewButton';
import { filterVideoComments, getFirstVideoThumbnail, hasVideoComments, scrollToSectionAndFocus } from '../../utils';

type Result = {
	videoPreview: ReactNode;
};

export function useMomentumBarVideoPreview(
	trackVideoPreviewView: (petitionId: string) => void,
	scrollToSection: (sectionId: SectionId) => void,
): Result {
	const {
		data: {
			petition: { id: petitionId },
			supporterVoicesVideos: { videos },
		},
	} = usePetitionDetailsPageData();
	const { translate } = useI18n();
	const hasTrackedPreviewView = useRef(false);

	const handleVideoPreviewAccess = useCallback(() => {
		if (hasTrackedPreviewView.current) return;
		hasTrackedPreviewView.current = true;
		trackVideoPreviewView(petitionId);
	}, [petitionId, trackVideoPreviewView]);

	const handleVideoPreviewBlur = useCallback(() => {
		hasTrackedPreviewView.current = false;
	}, []);

	const handleVideoPreviewClick = useCallback(() => {
		scrollToSectionAndFocus(SECTION_ID_MAPPING.SUPPORTER_VOICES, scrollToSection);
	}, [scrollToSection]);

	const hasVideos = hasVideoComments(videos);
	const firstVideoThumbnail = useMemo(() => getFirstVideoThumbnail(videos, { width: 280, height: 420 }), [videos]);
	const firstVideoThumbnailAlt = useMemo(() => {
		// .at(0) (not [0]) so the type stays `T | undefined` when the filtered list is empty —
		// bracket indexing doesn't add `| undefined` here (noUncheckedIndexedAccess is off).
		const firstVideo = filterVideoComments(videos).at(0);

		// oxlint-disable-next-line typescript/no-unnecessary-condition -- a video comment can genuinely have no attributed user (see tests), even though the field isn't typed as optional
		const name = firstVideo?.user?.shortDisplayName;
		return name
			? translate('corgi.petition_details.momentum_bar.video_thumbnail_alt_with_name', { name })
			: translate('corgi.petition_details.momentum_bar.video_thumbnail_alt');
	}, [videos, translate]);

	const videoPreview = useMemo(
		() =>
			hasVideos && firstVideoThumbnail ? (
				<VideoPreviewButton
					thumbnailAlt={firstVideoThumbnailAlt}
					thumbnailUrl={firstVideoThumbnail}
					ariaLabel={translate('corgi.petition_details.momentum_bar.video_preview_aria_label')}
					onAccess={handleVideoPreviewAccess}
					onBlur={handleVideoPreviewBlur}
					onClick={handleVideoPreviewClick}
					testIdPrefix="momentum-bar"
				/>
			) : null,
		[
			hasVideos,
			firstVideoThumbnail,
			firstVideoThumbnailAlt,
			translate,
			handleVideoPreviewAccess,
			handleVideoPreviewBlur,
			handleVideoPreviewClick,
		],
	);

	return { videoPreview };
}
